const RETURN_PATH_KEY = 'authReturnPath';
const DEFAULT_PATH = '/dashboard';
// Case-insensitive because routing is: the sign-in screen renders on "/LOGIN" too.
const AUTH_PATH = /^\/(login|auth)(\/|$)/i;
// Long enough for a provider round trip with an MFA prompt; short enough that an attempt someone
// walked away from does not decide where the next person on the tab lands.
const REMEMBER_FOR_MS = 10 * 60 * 1000;

const parse = (path: string): URL | null => {
  try {
    return new URL(path, window.location.origin);
  } catch {
    return null;
  }
};

/**
 * The page to land on after sign-in. The path is resolved against this origin and kept only if it
 * stays here: a browser reads "//host", "/\host" and "/\t/host" as another site, so trusting the
 * raw string would turn sign-in into an open redirect. The returned pathname is checked as well as
 * the origin: parsing collapses dot segments, so a same-origin "/.//host" comes back as "//host".
 * Auth pages fall back too — landing on one after signing in would loop.
 */
export function safeReturnPath(path: string | null | undefined): string {
  if (!path?.startsWith('/')) return DEFAULT_PATH;
  const url = parse(path);
  if (!url || url.origin !== window.location.origin || url.pathname.startsWith('//')) return DEFAULT_PATH;
  if (AUTH_PATH.test(url.pathname)) return DEFAULT_PATH;
  return url.pathname + url.search + url.hash;
}

// A path that will not parse is treated as an auth page: never remembered, never landed on.
const isAuthPage = (path: string) => {
  const url = parse(path);
  return !url || AUTH_PATH.test(url.pathname);
};

/**
 * SSO leaves the app for the provider, so the requested page survives the round trip here. A sign-in
 * page is never the page someone asked for — reaching /login means a failed callback's "Try again" —
 * so starting from one leaves the page remembered by the earlier attempt in place.
 */
export function rememberReturnPath(currentPath: string): void {
  if (isAuthPage(currentPath)) return;
  sessionStorage.setItem(RETURN_PATH_KEY, JSON.stringify({ path: safeReturnPath(currentPath), at: Date.now() }));
}

export function forgetReturnPath(): void {
  sessionStorage.removeItem(RETURN_PATH_KEY);
}

export function takeReturnPath(): string {
  const stored = sessionStorage.getItem(RETURN_PATH_KEY);
  forgetReturnPath();
  try {
    const { path, at } = JSON.parse(stored ?? '');
    if (typeof at !== 'number' || Date.now() - at > REMEMBER_FOR_MS) return DEFAULT_PATH;
    return safeReturnPath(typeof path === 'string' ? path : null);
  } catch {
    return DEFAULT_PATH;
  }
}

/** Where a sign-in completed on `currentPath` lands; the remembered page is spent either way. */
export function takeReturnPathFrom(currentPath: string): string {
  const remembered = takeReturnPath();
  return isAuthPage(currentPath) ? remembered : safeReturnPath(currentPath);
}
